import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { FontSizes, TemplateState } from '../../../../types/common/types';
import { PackageScanModesFormViewData } from '../types/PackageScanModesFormViewData';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../ui/gzRadio';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { translateFn } from '../../../../utils/translator.helper';
import { ScanModes } from '../types/ScanModes';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { ScanModeAutomaticStrings, ScanModeData } from '../types/ScanModeData';
import { ScanTypes } from '../types/ScanTypes';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { Feature } from '@nirvana/shared/constants';
import { PackageScanModesEvent } from '../types/PackageScanModesEvent';
import { IntegrationType } from '../../../../company/types/IntegrationType';
import { PackageActionContext } from '../../types/PackageActionContext';
import { DeepPartial } from 'typeorm';
import { PackageData } from '../../../types/package-data';
import { ProtectedEntitiesOptions } from '../types/ProtectedEntitiesTypes';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { PackageType } from '../../../packagesGrid/types';
import { GzTextWebComponent } from '../../../../ui/gzText';

export class PackageScanModesFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  private viewData: PackageScanModesFormViewData;
  private contextIsFrat: boolean;

  public dom: {
    $wrapper: HTMLElement;
    $packageScanModesTitle: GzTitleWebComponent;
    $packageScanModeSubtitle: GzTextWebComponent;

    $packageScanModesFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
    $packageScanModesGroup: GzRadioGroupWebComponent;
    $packageScanModeAutomatic: GzRadioWebComponent;
    $packageScanModeCustom: GzRadioWebComponent;
    $packageScanModeAutomaticSubtitle: GzTextWebComponent;
    $packageScanModeAutomaticWarning: GzInfoBoxWebComponent;

    $packageScanModeCustomWrapper: HTMLElement;

    $packageScanModeComputers: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageScanModeComputersFallback: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageScanModeComputersFallbackCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

    $packageScanModeVirtualMachines: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageScanModeVirtualMachinesFallback: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageScanModeVirtualMachinesFallbackCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

    $packageScanModeEc2Wrapper: HTMLElement;
    $packageScanModeContainersEc2: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageScanModeContainersEc2Fallback: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageScanModeContainersEc2FallbackCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

    $packageScanModeAzureWrapper: HTMLElement;
    $packageScanModeContainersAzure: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageScanModeContainersAzureFallback: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $packageScanModeContainersAzureFallbackCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  };

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('#package-scan-modes-form-wrapper'),
      $packageScanModeCustomWrapper: this.shadow.querySelector('#custom-wrapper'),
      $packageScanModesTitle: this.shadow.querySelector('#package-scan-mode-title'),
      $packageScanModeSubtitle: this.shadow.querySelector('#package-scan-mode-subtitle'),

      $packageScanModesFormField: this.shadow.querySelector('#package-scan-modes-form-field'),
      $packageScanModeAutomaticSubtitle: this.shadow.querySelector('#package-scan-mode-automatic-subtitle'),
      $packageScanModeAutomaticWarning: this.shadow.querySelector('#central-scan-warning'),
      $packageScanModesGroup: this.shadow.querySelector('#package-scan-modes-group'),
      $packageScanModeAutomatic: this.shadow.querySelector('#package-scan-mode-automatic'),
      $packageScanModeCustom: this.shadow.querySelector('#package-scan-mode-custom'),

      $packageScanModeComputers: this.shadow.querySelector('#scan-modes-computers'),
      $packageScanModeComputersFallback: this.shadow.querySelector('#scan-modes-computers-fallback'),
      $packageScanModeComputersFallbackCheckbox: this.shadow.querySelector('#scan-modes-computers-fallback-checkbox'),

      $packageScanModeVirtualMachines: this.shadow.querySelector('#scan-modes-virtual-machines'),
      $packageScanModeVirtualMachinesFallback: this.shadow.querySelector('#scan-modes-virtual-machines-fallback'),
      $packageScanModeVirtualMachinesFallbackCheckbox: this.shadow.querySelector(
        '#scan-modes-virtual-machines-fallback-checkbox'
      ),

      $packageScanModeEc2Wrapper: this.shadow.querySelector('#ec2-wrapper'),
      $packageScanModeContainersEc2: this.shadow.querySelector('#scan-modes-containers-ec2'),
      $packageScanModeContainersEc2Fallback: this.shadow.querySelector('#scan-modes-containers-ec2-fallback'),
      $packageScanModeContainersEc2FallbackCheckbox: this.shadow.querySelector(
        '#scan-modes-containers-ec2-fallback-checkbox'
      ),

      $packageScanModeAzureWrapper: this.shadow.querySelector('#azure-wrapper'),
      $packageScanModeContainersAzure: this.shadow.querySelector('#scan-modes-containers-azure'),
      $packageScanModeContainersAzureFallback: this.shadow.querySelector('#scan-modes-containers-azure-fallback'),
      $packageScanModeContainersAzureFallbackCheckbox: this.shadow.querySelector(
        '#scan-modes-containers-azure-fallback-checkbox'
      ),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$packageScanModesTitle.innerText = this.dependencies.translateFn('Scan Modes');
    this.dom.$packageScanModeSubtitle.innerText = translateFn('On macOS, only Local Scan applies');
    this.dom.$packageScanModeAutomatic.label = translateFn('Automatic');
    this.dom.$packageScanModeCustom.label = translateFn('Custom');
    this.dom.$packageScanModeAutomaticWarning.innerHTML = translateFn(
      'For Central Scan, it is mandatory to firstly deploy a Security Server'
    );

    this.dom.$packageScanModeComputers.label = translateFn('For computers');
    this.dom.$packageScanModeComputers.Input.noResultsFound = this.commonStrings.noResultsFound;
    this.dom.$packageScanModeComputersFallbackCheckbox.Input.label = translateFn('Fallback');

    this.dom.$packageScanModeVirtualMachines.label = translateFn('For virtual machines');
    this.dom.$packageScanModeVirtualMachines.Input.noResultsFound = this.commonStrings.noResultsFound;
    this.dom.$packageScanModeVirtualMachinesFallbackCheckbox.Input.label = translateFn('Fallback');

    this.dom.$packageScanModeContainersEc2.label = translateFn('For EC2 instances');
    this.dom.$packageScanModeContainersEc2.Input.noResultsFound = this.commonStrings.noResultsFound;
    this.dom.$packageScanModeContainersEc2FallbackCheckbox.Input.label = translateFn('Fallback');

    this.dom.$packageScanModeContainersAzure.label = translateFn('For Azure instances');
    this.dom.$packageScanModeContainersAzure.Input.noResultsFound = this.commonStrings.noResultsFound;
    this.dom.$packageScanModeContainersAzureFallbackCheckbox.Input.label = translateFn('Fallback');
  }

  buildSuccessView(viewData: PackageScanModesFormViewData): void {
    this.viewData = viewData;
    toggleVisibility(this.dom.$packageScanModeCustomWrapper, false);
    toggleVisibility(this.dom.$packageScanModeEc2Wrapper, false);
    toggleVisibility(this.dom.$packageScanModeAzureWrapper, false);

    this.dom.$packageScanModeAutomatic.value = ScanModes.Automatic;
    this.dom.$packageScanModeCustom.value = ScanModes.Custom;
    this.dom.$packageScanModeAutomatic.label.fontsize(FontSizes.PARAGRAPH_13);
    this.dom.$packageScanModeCustom.label.fontsize(FontSizes.PARAGRAPH_13);

    this.contextIsFrat = viewData.contextIsFrat;
    //on create mode always use automatic
    if (this.viewData.formContext === PackageActionContext.Create && !this.contextIsFrat) {
      this.setScanModeAuto(viewData.features);
    } else if (this.contextIsFrat && this.viewData.formContext === PackageActionContext.Create) {
      this.setDefaultValuesForFratContext();
    }

    this.createFormGroup();
    this.formGroup.setDisabled(true);
  }

  buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      settings: {
        scanMode: {
          type: this.dom.$packageScanModesFormField,
          computers: {
            firstOption: this.dom.$packageScanModeComputers,
            fallback: this.dom.$packageScanModeComputersFallbackCheckbox,
            secondOption: this.dom.$packageScanModeComputersFallback,
          },
          vms: {
            firstOption: this.dom.$packageScanModeVirtualMachines,
            fallback: this.dom.$packageScanModeVirtualMachinesFallbackCheckbox,
            secondOption: this.dom.$packageScanModeVirtualMachinesFallback,
          },
          ec2: {
            firstOption: this.dom.$packageScanModeContainersEc2,
            fallback: this.dom.$packageScanModeContainersEc2FallbackCheckbox,
            secondOption: this.dom.$packageScanModeContainersEc2Fallback,
          },
          azure: {
            firstOption: this.dom.$packageScanModeContainersAzure,
            fallback: this.dom.$packageScanModeContainersAzureFallbackCheckbox,
            secondOption: this.dom.$packageScanModeContainersAzureFallback,
          },
        },
      },
    });
  }

  public setDefaultValuesForFratContext(): void {
    this.dom.$packageScanModesFormField.Input.setFormValue(ScanModes.Custom);
    this.dom.$packageScanModeComputers.Input.options = this.viewData.scanModes;
    this.dom.$packageScanModeComputers.Input.setFormValue(ScanTypes.Local);
    this.dom.$packageScanModeVirtualMachines.Input.options = this.viewData.scanModes;
    this.dom.$packageScanModeVirtualMachines.Input.setFormValue(ScanTypes.Local);
  }

  private setScanModesOptions(
    formField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>,
    scanModes: ScanModeData[],
    defaultScanMode: number
  ): void {
    formField.Input.options = scanModes;
    formField.Input.value = defaultScanMode;
    formField.Input.setSelectedOption();
    formField.initialValue = formField.Input.value;
  }

  /**
   * This method sets the default values for dropdown/checkboxes in scan modes section and assigns the dropdown options based on feature.
   * It toggles the visibility of Ec2 and Azure components based on integration.
   *
   * @param {Feature[]} availableFeatures
   */
  public setCustomScanModeFieldValues(availableFeatures: Feature[] = []): void {
    const showScanModeWithSva: boolean = !!availableFeatures?.[Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING_INSTALL];
    const computersScanModes: ScanModeData[] = showScanModeWithSva
      ? this.viewData.scanModes
      : this.viewData.scanModes.filter(scanMode => {
          return scanMode.value !== ScanTypes.PrivateCloud;
        });

    const hasEc2 = this.dependencies.isCloud()
      ? this.dependencies.IntegrationsLib.self.hasCloudIntegrationVisible(IntegrationType.Aws)
      : this.dependencies.hasAwsIntegration();
    const hasAzure = this.dependencies.isGZ() && this.dependencies.hasAzureIntegration();
    toggleVisibility(this.dom.$packageScanModeEc2Wrapper, hasEc2);
    toggleVisibility(this.dom.$packageScanModeAzureWrapper, hasAzure);

    this.setScanModesOptions(
      this.dom.$packageScanModeComputers,
      computersScanModes,
      showScanModeWithSva ? ScanTypes.PrivateCloud : ScanTypes.Cloud
    );
    this.dom.$packageScanModeComputersFallbackCheckbox.Input.checked = showScanModeWithSva;
    this.dom.$packageScanModeComputersFallbackCheckbox.initialValue =
      this.dom.$packageScanModeComputersFallbackCheckbox.Input.checked;
    this.setScanModesOptions(
      this.dom.$packageScanModeComputersFallback,
      this.viewData.simpleScanModes,
      ScanTypes.Local
    );

    this.setScanModesOptions(
      this.dom.$packageScanModeVirtualMachines,
      computersScanModes,
      showScanModeWithSva ? ScanTypes.PrivateCloud : ScanTypes.Cloud
    );
    this.dom.$packageScanModeVirtualMachinesFallbackCheckbox.Input.checked =
      this.dependencies.isGZ() && showScanModeWithSva;
    this.dom.$packageScanModeVirtualMachinesFallbackCheckbox.initialValue =
      this.dom.$packageScanModeVirtualMachinesFallbackCheckbox.Input.checked;
    this.setScanModesOptions(
      this.dom.$packageScanModeVirtualMachinesFallback,
      this.viewData.simpleScanModes,
      ScanTypes.Local
    );

    this.setScanModesOptions(
      this.dom.$packageScanModeContainersEc2,
      this.dependencies.isCloud() ? this.viewData.ec2ScanModes : this.viewData.scanModes,
      this.dependencies.isCloud() ? ScanTypes.PrivateCloud : ScanTypes.Local
    );
    this.dom.$packageScanModeContainersEc2FallbackCheckbox.Input.checked = this.dependencies.isCloud();
    this.dom.$packageScanModeContainersEc2FallbackCheckbox.initialValue =
      this.dom.$packageScanModeContainersEc2FallbackCheckbox.Input.checked;
    this.dom.$packageScanModeContainersEc2FallbackCheckbox.Input.disabled = this.dependencies.isCloud();
    this.setScanModesOptions(
      this.dom.$packageScanModeContainersEc2Fallback,
      this.viewData.simpleScanModes,
      this.dependencies.isCloud() ? ScanTypes.Cloud : ScanTypes.Local
    );

    this.setScanModesOptions(this.dom.$packageScanModeContainersAzure, this.viewData.scanModes, ScanTypes.Local);
    this.dom.$packageScanModeContainersAzureFallbackCheckbox.Input.checked = false;
    this.dom.$packageScanModeContainersAzureFallbackCheckbox.initialValue =
      this.dom.$packageScanModeContainersAzureFallbackCheckbox.Input.checked;
    this.setScanModesOptions(
      this.dom.$packageScanModeContainersAzureFallback,
      this.viewData.simpleScanModes,
      ScanTypes.Local
    );
  }

  /**
   * this event is used to populate fallback actions based on first option
   */
  public dispatchInitEvent(): void {
    const customModeOptions = this.getCustomModeOptions();
    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(PackageScanModesEvent.OnInit, {
        detail: customModeOptions,
        bubbles: true,
        composed: true,
      })
    );
  }

  public getCustomModeOptions(): ProtectedEntitiesOptions {
    return {
      computers: {
        firstOption: this.dom.$packageScanModeComputers.Input.value as number,
      },
      vms: {
        firstOption: this.dom.$packageScanModeVirtualMachines.Input.value as number,
      },
      ec2: {
        firstOption: this.dom.$packageScanModeContainersEc2.Input.value as number,
      },
      azure: {
        firstOption: this.dom.$packageScanModeContainersAzure.Input.value as number,
      },
    };
  }

  public setFormFieldAutoForEdr(): void {
    this.dom.$packageScanModesFormField.Input.setFormValue(ScanModes.Automatic);
    this.dom.$packageScanModesFormField.initialValue = this.dom.$packageScanModesFormField.value;
  }

  public setScanModeAuto(availableFeatures: Feature[] = []): void {
    if (this.contextIsFrat) {
      return;
    }
    this.dom.$packageScanModeAutomatic.value = ScanModes.Automatic;
    this.dom.$packageScanModesFormField.Input.setFormValue(ScanModes.Automatic);
    this.dom.$packageScanModesFormField.initialValue = this.dom.$packageScanModesFormField.value;
    this.setScanModeAutoText(availableFeatures);
  }

  /**
   * This methods sets the automatic scan mode text based on features and integrations.
   * @param {Feature[]} availableFeatures
   */
  public setScanModeAutoText(availableFeatures: Feature[] = []): void {
    this.dom.$packageScanModeAutomaticSubtitle.innerText = '';

    const showScanModeWithSva: boolean = !!availableFeatures?.[Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING_INSTALL];

    const hasEc2 = this.dependencies.isCloud()
      ? this.dependencies.IntegrationsLib.self.hasCloudIntegrationVisible(IntegrationType.Aws)
      : this.dependencies.hasAwsIntegration();
    const hasAzure = this.dependencies.isGZ() && this.dependencies.hasAzureIntegration();
    this.translations = this.getScanModeAutoStrings(hasEc2, hasAzure);

    const mergeText = showScanModeWithSva
      ? this.translations.scanModeAutoWithSva
      : this.translations.scanModeAutoWithoutSva;
    for (const text of Object.values(mergeText)) {
      this.dom.$packageScanModeAutomaticSubtitle.innerHTML += `<span>${text}</span>`;
    }
    toggleVisibility(this.dom.$packageScanModeAutomaticWarning, showScanModeWithSva);
  }

  private getScanModeAutoStrings(hasEc2: boolean, hasAzure: boolean): ScanModeAutomaticStrings {
    const ec2InstancesScan = this.dependencies.isCloud()
      ? translateFn('Central Scan with fallback on public Hybrid Scan for EC2 instances\n')
      : translateFn('Local scan for EC2 instances\n');

    return {
      scanModeAutoWithSva: {
        lowHardwarePerformanceComputerScan: translateFn(
          'Central Scan with fallback on Hybrid Scan for computers with low hardware performance'
        ),
        highHardwarePerformanceComputerScan: translateFn('Local Scan for computers with high hardware performance'),
        virtualMachinesScan: translateFn('Central Scan with fallback on Hybrid Scan for virtual machines'),
        ec2InstancesScan: hasEc2 ? ec2InstancesScan : '',
        azureInstancesScan: hasAzure ? translateFn('Local scan for Azure instances') : '',
      },
      scanModeAutoWithoutSva: {
        lowHardwarePerformanceComputerScan: translateFn('Hybrid Scan for computers with low hardware performance\n'),
        highHardwarePerformanceComputerScan: translateFn('Local Scan for computers with high hardware performance\n'),
        ec2InstancesScan: hasEc2 ? ec2InstancesScan : '',
        azureInstancesScan: hasAzure ? translateFn('Local scan for Azure instances') : '',
        virtualMachinesScan: translateFn('Hybrid Scan for virtual machines'),
      },
    };
  }

  /**
   * This sends a specific form value for automatic mode.
   *
   * @returns {DeepPartial<PackageData>}
   */
  public getAutomaticFormValue(): DeepPartial<PackageData> {
    const data: DeepPartial<PackageData> = {
      settings: {
        scanMode: {
          type: ScanModes.Automatic,
          computers: {
            firstOption: ScanTypes.Local,
            fallback: false,
          },
          vms: {
            firstOption: ScanTypes.Local,
            fallback: false,
          },
          ec2: {
            firstOption: this.dependencies.isCloud() ? ScanTypes.PrivateCloud : ScanTypes.Local,
            fallback: this.dependencies.isCloud(),
          },
          azure: {
            firstOption: ScanTypes.Local,
            fallback: false,
          },
        },
      },
    };

    if (data.settings?.scanMode?.ec2 && data.settings?.scanMode?.ec2?.fallback === true) {
      data.settings.scanMode.ec2.secondOption = this.dependencies.isCloud() ? ScanTypes.Cloud : ScanTypes.Local;
    }

    return data;
  }

  /**
   * This method sets the visibility of the fallback checkbox and dropdown based on isVisible parameter.
   * The dropdown disabled property depends on the checkbox state.
   *
   * @param {GzFormFieldWebComponent<GzDropdownWebComponent, string | number>} fallback
   * @param {GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>} checkbox
   * @param {boolean} isVisible
   */
  public changeStateFallback(
    fallback: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>,
    checkbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>,
    isVisible: boolean
  ): void {
    toggleVisibility(fallback, isVisible);
    toggleVisibility(checkbox, isVisible);
    fallback.Input.disabled = !checkbox.Input.checked;
  }

  public scanModeRadioInputChanged(selectedValue, availableFeatures: Feature[] = []): void {
    switch (selectedValue) {
      case ScanModes.Automatic:
        this.setScanModeAutoText(availableFeatures);
        toggleVisibility(this.dom.$packageScanModeCustomWrapper, false);
        toggleVisibility(this.dom.$packageScanModeAutomaticSubtitle, true);
        break;
      case ScanModes.Custom:
        toggleVisibility(this.dom.$packageScanModeAutomaticWarning, false);
        toggleVisibility(this.dom.$packageScanModeCustomWrapper, true);
        toggleVisibility(this.dom.$packageScanModeAutomaticSubtitle, false);
        break;
    }
  }

  /**
   * Used on edit : sets the form values based on packageData
   *
   * @param packageData
   * @param {Feature[]} availableFeatures
   * @returns {Promise<void>}
   */
  public async setPackageData(packageData, availableFeatures: Feature[]): Promise<void> {
    this.contextIsFrat = packageData.type === PackageType.Frat || packageData.type === PackageType.RelayForFrat;
    if (!this.contextIsFrat) {
      this.formGroup.setDisabled(false);
    }
    const scanMode = packageData.settings.scanMode;

    if (!scanMode) {
      return;
    }

    if (scanMode.type === ScanModes.Automatic) {
      this.setScanModeAuto(availableFeatures);
      return;
    }
    this.dom.$packageScanModesFormField.Input.setFormValue(scanMode.type);
    this.dom.$packageScanModesFormField.initialValue = this.dom.$packageScanModesFormField.value;

    //first init customMode options
    this.setCustomScanModeFieldValues(availableFeatures);
    if (scanMode.computers) {
      this.dom.$packageScanModeComputers.Input.value = scanMode.computers.firstOption;
      this.dom.$packageScanModeComputers.initialValue = this.dom.$packageScanModeComputers.value;
      this.dom.$packageScanModeComputers.Input.setSelectedOption();
      this.dom.$packageScanModeComputersFallbackCheckbox.Input.checked = scanMode.computers.fallback ?? true;
      this.dom.$packageScanModeComputersFallback.Input.disabled = !(scanMode.computers.fallback ?? true);
      this.dom.$packageScanModeComputersFallbackCheckbox.initialValue =
        this.dom.$packageScanModeComputersFallbackCheckbox.value;
      if (scanMode.computers.secondOption) {
        this.dom.$packageScanModeComputersFallback.Input.value = scanMode.computers.secondOption;
        this.dom.$packageScanModeComputersFallback.Input.setSelectedOption();
        this.dom.$packageScanModeComputersFallback.initialValue = this.dom.$packageScanModeComputersFallback.value;
      }
    }

    if (scanMode.vms) {
      this.dom.$packageScanModeVirtualMachines.Input.value = scanMode.vms.firstOption;
      this.dom.$packageScanModeVirtualMachines.Input.setSelectedOption();
      this.dom.$packageScanModeVirtualMachines.initialValue = this.dom.$packageScanModeVirtualMachines.value;
      this.dom.$packageScanModeVirtualMachinesFallbackCheckbox.Input.checked = scanMode.vms.fallback ?? true;
      this.dom.$packageScanModeVirtualMachinesFallback.Input.disabled = !(scanMode.vms.fallback ?? true);
      this.dom.$packageScanModeVirtualMachinesFallbackCheckbox.initialValue =
        this.dom.$packageScanModeVirtualMachinesFallbackCheckbox.value;
      if (scanMode.vms.secondOption) {
        this.dom.$packageScanModeVirtualMachinesFallback.Input.value = scanMode.vms.secondOption;
        this.dom.$packageScanModeVirtualMachinesFallback.Input.setSelectedOption();
        this.dom.$packageScanModeVirtualMachinesFallback.initialValue =
          this.dom.$packageScanModeVirtualMachinesFallback.value;
      }
    }

    if (scanMode.ec2) {
      this.dom.$packageScanModeContainersEc2.Input.value = scanMode.ec2.firstOption;
      this.dom.$packageScanModeContainersEc2.Input.setSelectedOption();
      this.dom.$packageScanModeContainersEc2.initialValue = this.dom.$packageScanModeContainersEc2.value;
      this.dom.$packageScanModeContainersEc2FallbackCheckbox.Input.checked = scanMode.ec2.fallback ?? true;
      this.dom.$packageScanModeContainersEc2Fallback.Input.disabled = !(scanMode.ec2.fallback ?? true);
      this.dom.$packageScanModeContainersEc2FallbackCheckbox.initialValue =
        this.dom.$packageScanModeContainersEc2FallbackCheckbox.value;
      if (scanMode.ec2.secondOption) {
        this.dom.$packageScanModeContainersEc2Fallback.Input.value = scanMode.ec2.secondOption;
        this.dom.$packageScanModeContainersEc2Fallback.Input.setSelectedOption();
        this.dom.$packageScanModeContainersEc2Fallback.initialValue =
          this.dom.$packageScanModeContainersEc2Fallback.value;
      }
    }

    if (scanMode.azure) {
      this.dom.$packageScanModeContainersAzure.Input.value = scanMode.azure.firstOption;
      this.dom.$packageScanModeContainersAzure.Input.setSelectedOption();
      this.dom.$packageScanModeContainersAzure.initialValue = this.dom.$packageScanModeContainersAzure.value;
      this.dom.$packageScanModeContainersAzureFallbackCheckbox.Input.checked = scanMode.azure.fallback ?? true;
      this.dom.$packageScanModeContainersAzureFallback.Input.disabled = !(scanMode.azure.fallback ?? true);
      this.dom.$packageScanModeContainersAzureFallbackCheckbox.initialValue =
        this.dom.$packageScanModeContainersAzureFallbackCheckbox.value;
      if (scanMode.azure.secondOption) {
        this.dom.$packageScanModeContainersAzureFallback.Input.value = scanMode.azure.secondOption;
        this.dom.$packageScanModeContainersAzureFallback.Input.setSelectedOption();
        this.dom.$packageScanModeContainersAzureFallback.initialValue =
          this.dom.$packageScanModeContainersAzureFallback.value;
      }
    }
    this.dispatchInitEvent();
  }
}
